import { useCallback, useMemo, useState } from 'react';
import {
  applyTaskFilters,
  getValidConditions,
  normalizeConditions,
  conditionsFromSavedFilter
} from '../components/reporting/filters/filterEngine';

export function useTaskQueryFilters() {
  const [searchChips, setSearchChips] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterDialogOpen = Boolean(filterAnchorEl);

  const openFilterDialog = useCallback((event) => {
    setFilterAnchorEl(event?.currentTarget ?? null);
  }, []);

  const closeFilterDialog = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  const setFilterDialogOpen = useCallback((open) => {
    if (!open) setFilterAnchorEl(null);
  }, []);

  const validConditions = useMemo(() => getValidConditions(conditions), [conditions]);
  const hasActiveConditions = validConditions.length > 0;

  const addSearchChip = useCallback((rawValue) => {
    const value = String(rawValue || '').trim();
    if (!value) return;
    setSearchChips((prev) => (
      prev.some((chip) => chip.toLowerCase() === value.toLowerCase())
        ? prev
        : [...prev, value]
    ));
  }, []);

  const removeSearchChip = useCallback((chipToRemove) => {
    setSearchChips((prev) => prev.filter((chip) => chip !== chipToRemove));
  }, []);

  const removeCondition = useCallback((conditionId) => {
    setConditions((prev) => prev.filter((condition) => condition.id !== conditionId));
  }, []);

  const loadConditions = useCallback((nextConditions) => {
    setConditions(normalizeConditions(nextConditions));
  }, []);

  const clearConditions = useCallback(() => {
    setConditions([]);
  }, []);

  const applySavedFilter = useCallback((filter) => {
    setConditions(conditionsFromSavedFilter(filter));
  }, []);

  const applyToRows = useCallback((rows, searchFields) => (
    applyTaskFilters(rows, { searchChips, conditions, searchFields })
  ), [searchChips, conditions]);

  return {
    searchChips,
    setSearchChips,
    addSearchChip,
    removeSearchChip,
    conditions,
    setConditions,
    validConditions,
    hasActiveConditions,
    loadConditions,
    clearConditions,
    removeCondition,
    applySavedFilter,
    applyToRows,
    filterDialogOpen,
    filterAnchorEl,
    openFilterDialog,
    closeFilterDialog,
    setFilterDialogOpen
  };
}
